/**
 * The decision-tree API as the frontend sees it: shapes, errors, and the tree-level checks.
 *
 * ## Why an interface and not just calls to the generated client
 *
 * The nine operations exist in `SalesRecommendationHubModel` but not yet in
 * `@amzn/salesrecommendationhub-client`: the model change has not been published to a version
 * set, so there is no `PutDecisionTreeDraftCommand` to import. Meanwhile the parts of this
 * feature that are hardest to get right — the autosave trigger, the conflict banner, the
 * three-way merge — cannot be exercised at all without something to call.
 *
 * So the shape is declared here and implemented twice: `MockDecisionTreeApi` now, and a thin
 * adapter over the generated commands later. Swapping them is one line in `apiProvider`.
 *
 * ## Why the mock enforces the real rules
 *
 * A mock that always succeeds would make the UI look finished and hide every case worth
 * building: the stale autosave from a second tab, the commit that loses a race, the document
 * that fails validation. The mock rejects exactly where the service will, with the same
 * errors, so the handling is written against real behaviour rather than imagined behaviour.
 *
 * ## Why this file is not called DecisionTreeApi.ts
 *
 * It was, and `decisionTreeApi.ts` (the factory) silently overwrote it: macOS is
 * case-insensitive, so the two names are one file. The factory is `apiProvider.ts` now and
 * this is `treeApiTypes.ts` — two names that differ in more than case, which is the only
 * version of this that survives a checkout on either kind of filesystem.
 */
import type { DecisionTreeDocument } from '../utils/wire';

/* ── Shapes, mirroring the Smithy model ─────────────────────────────────────── */

export type DecisionTreeVersionStatus = 'draft' | 'published' | 'archived';

export interface DecisionTreeVersionData {
  treeId: string;
  version: number;
  /** The authoring document, as JSON. Opaque to the service; parsed only by the caller. */
  document: string;
  schemaVersion: number;
  status: DecisionTreeVersionStatus;
  baseVersion?: number;
  mergedFrom?: number[];
  documentSha256?: string;
  note?: string;
  author?: string;
  createdAt: string;
  updatedAt: string;
  publishedBy?: string;
  publishedAt?: string;
}

/** A version without its document — see the model's note on the 1 MB query limit. */
export interface DecisionTreeVersionSummary {
  treeId: string;
  version: number;
  status: DecisionTreeVersionStatus;
  baseVersion?: number;
  mergedFrom?: number[];
  note?: string;
  author?: string;
  createdAt: string;
  updatedAt: string;
  changeSummary?: { added: number; modified: number; removed: number };
}

export interface DecisionTreeDraftData {
  treeId: string;
  owner: string;
  document: string;
  schemaVersion: number;
  baseVersion: number;
  createdAt: string;
  updatedAt: string;
}

export interface DecisionTreeEditor {
  owner: string;
  baseVersion: number;
  updatedAt: string;
}

/**
 * Returned by every draft-facing operation, including the autosave.
 *
 * That is the point: the autosave already fires every few seconds, so "someone published" and
 * "someone else is editing" are known within one interval at zero extra requests.
 */
export interface DecisionTreeEditingContext {
  headVersion: number;
  activeVersion?: number;
  otherEditors: DecisionTreeEditor[];
}

export interface TreeProblem {
  code: string;
  message: string;
  questionId?: string;
  transitionId?: string;
}

/* ── Errors ─────────────────────────────────────────────────────────────────── */

export class ConflictError extends Error {
  override readonly name = 'ConflictException';
}

/** Carries the head so the client can fetch base and theirs without another round trip. */
export class DecisionTreeConflictError extends Error {
  override readonly name = 'DecisionTreeConflictException';
  constructor(
    message: string,
    readonly headVersion: number,
    readonly headAuthor?: string,
    readonly headCreatedAt?: string,
  ) {
    super(message);
  }
}

/** Every problem at once: one at a time would mean one round trip per mistake. */
export class DecisionTreeValidationError extends Error {
  override readonly name = 'DecisionTreeValidationException';
  constructor(
    message: string,
    readonly problems: TreeProblem[],
  ) {
    super(message);
  }
}

export class NotFoundError extends Error {
  override readonly name = 'ResourceNotFoundException';
}

/* ── The interface ──────────────────────────────────────────────────────────── */

export interface DecisionTreeApi {
  createDraft(input: {
    treeId: string;
    baseVersion?: number;
  }): Promise<{ draft: DecisionTreeDraftData; context: DecisionTreeEditingContext }>;

  getDraft(input: {
    treeId: string;
  }): Promise<{ draft: DecisionTreeDraftData; context: DecisionTreeEditingContext }>;

  putDraft(input: {
    treeId: string;
    document: string;
    schemaVersion: number;
    expectedUpdatedAt: string;
  }): Promise<{ draft: DecisionTreeDraftData; context: DecisionTreeEditingContext }>;

  deleteDraft(input: { treeId: string; expectedUpdatedAt: string }): Promise<void>;

  listEditors(input: { treeId: string }): Promise<{ context: DecisionTreeEditingContext }>;

  commitVersion(input: {
    treeId: string;
    expectedDraftUpdatedAt: string;
    documentSha256: string;
    baseVersion: number;
    note: string;
    mergedFrom?: number[];
  }): Promise<{ version: DecisionTreeVersionData; draftDeleted: boolean }>;

  getVersion(input: {
    treeId: string;
    version: number;
  }): Promise<{ version: DecisionTreeVersionData }>;

  listVersions(input: {
    treeId: string;
    maxResults?: number;
  }): Promise<{ versions: DecisionTreeVersionSummary[]; nextToken?: string }>;

  publishVersion(input: {
    treeId: string;
    version: number;
    expectedUpdatedAt: string;
  }): Promise<{ version: DecisionTreeVersionData }>;
}

/* ── Validation, the same checks the service will run ──────────────────────── */

/**
 * The tree-level checks a shape validator cannot make.
 *
 * Deliberately the same list the model's `TreeProblem.code` enumerates, because the UI has to
 * render these and will be written against whatever this returns first.
 */
export function validateDocument(doc: DecisionTreeDocument): TreeProblem[] {
  const problems: TreeProblem[] = [];
  const ids = new Set(doc.questions.map((q) => q.questionId));

  if (!doc.entryQuestionId || !ids.has(doc.entryQuestionId)) {
    problems.push({
      code: 'NO_ENTRY_QUESTION',
      message: `入口问题「${doc.entryQuestionId}」不存在`,
    });
  }

  for (const t of doc.transitions) {
    if (!ids.has(t.toQuestionId)) {
      problems.push({
        code: 'DANGLING_TRANSITION',
        message: `${t.fromQuestionId} 的规则指向 ${t.toQuestionId}，而它不存在`,
        questionId: t.fromQuestionId,
        transitionId: t.transitionId,
      });
    }
    if (!ids.has(t.fromQuestionId)) {
      problems.push({
        code: 'DANGLING_TRANSITION',
        message: `规则的来源 ${t.fromQuestionId} 不存在`,
        transitionId: t.transitionId,
      });
    }
  }

  /*
   * At most one unconditional way out, and only as the sole one.
   *
   * The engine stops evaluating conditions the moment it sees an unconditional edge, so a
   * second one does not merely duplicate — it silently disables every conditional sibling.
   */
  const outBySource = new Map<string, typeof doc.transitions>();
  for (const t of doc.transitions) {
    if (!outBySource.has(t.fromQuestionId)) outBySource.set(t.fromQuestionId, []);
    outBySource.get(t.fromQuestionId)!.push(t);
  }
  for (const [from, outs] of outBySource) {
    const unconditional = outs.filter((t) => !t.condition);
    if (unconditional.length > 1 || (unconditional.length === 1 && outs.length > 1)) {
      problems.push({
        code: 'MULTIPLE_UNCONDITIONAL_TRANSITIONS',
        message:
          `${from} 有 ${outs.length} 条出边，其中 ${unconditional.length} 条无条件 ` +
          `—— 引擎遇到无条件边就不再求值其它条件`,
        questionId: from,
      });
    }
  }

  for (const q of doc.questions) {
    const seen = new Set<string>();
    for (const o of q.options ?? []) {
      if (seen.has(o.optionValue)) {
        problems.push({
          code: 'DUPLICATE_OPTION_VALUE',
          message:
            `${q.questionId} 的选项 value「${o.optionValue}」重复 ` +
            `—— 规则按 value 比较，其中一个永远无法被单独命中`,
          questionId: q.questionId,
        });
      }
      seen.add(o.optionValue);
    }
  }

  // Reachability from the entry.
  const adj = new Map<string, string[]>();
  for (const t of doc.transitions) {
    if (!adj.has(t.fromQuestionId)) adj.set(t.fromQuestionId, []);
    adj.get(t.fromQuestionId)!.push(t.toQuestionId);
  }
  const reached = new Set<string>();
  const stack = [doc.entryQuestionId];
  while (stack.length) {
    const id = stack.pop()!;
    if (!id || reached.has(id)) continue;
    reached.add(id);
    for (const next of adj.get(id) ?? []) stack.push(next);
  }
  for (const q of doc.questions) {
    if (!reached.has(q.questionId)) {
      problems.push({
        code: 'UNREACHABLE_QUESTION',
        message: `${q.questionId} 从入口走不到`,
        questionId: q.questionId,
      });
    }
  }

  return problems;
}
